import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  key: string;
  placeholder: string;
  options: FilterOption[];
  minWidth?: string;
}

export interface FilterValues {
  search: string;
  filters: Record<string, string>;
}

@Component({
  selector: 'app-search-filter-bar',
  imports: [CommonModule, FormsModule, MatIconModule],
  template: `
    <div class="bg-white rounded-lg shadow p-4">
      <div class="flex flex-col gap-3">
        <div class="relative">
          <mat-icon class="absolute left-3 top-1.5 text-gray-400">search</mat-icon>
          <input
            type="text"
            [placeholder]="searchPlaceholder"
            class="w-full p-2 pl-10 border border-gray-200 rounded-lg text-sm input-field-focus"
            [(ngModel)]="searchValue"
            (ngModelChange)="onSearchChange()"
            (keydown.enter)="onSearchEnter()"
          />
        </div>
        <div *ngIf="visibleFilters.length > 0" class="flex gap-3 flex-wrap items-center">
          <div *ngFor="let filter of visibleFilters" class="relative">
            <select
              class="appearance-none py-2 px-3 pr-8 border border-gray-200 rounded-lg text-sm bg-white cursor-pointer hover:border-gray-300"
              [style.min-width]="filter.minWidth || '8rem'"
              [style.color]="filterValues[filter.key] ? '#374151' : '#9CA3AF'"
              [(ngModel)]="filterValues[filter.key]"
              (ngModelChange)="onFilterChange()"
            >
              <option value="" style="color: #9CA3AF;">{{ filter.placeholder }}</option>
              <option *ngFor="let option of filter.options" [value]="option.value" style="color: #374151;">
                {{ option.label }}
              </option>
            </select>
            <mat-icon class="absolute right-1 top-2 text-gray-400 pointer-events-none text-base">expand_more</mat-icon>
          </div>
          <button
            *ngIf="showClearButton && hasActiveFilters"
            type="button"
            class="h-[38px] px-4 border border-gray-200 text-gray-500 rounded-lg text-sm flex items-center gap-1.5 hover:bg-gray-50"
            (click)="clearFilters()"
          >
            <span class="text-lg leading-none">×</span>
            Limpar filtros
          </button>
        </div>
      </div>
    </div>
  `
})
export class SearchFilterBarComponent {
  @Input() searchPlaceholder = 'Buscar...';
  @Input() showClearButton = true;
  @Input() debounceTime = 300;

  @Output() searchChange = new EventEmitter<string>();
  @Output() filterChange = new EventEmitter<FilterValues>();
  @Output() searchEnter = new EventEmitter<string>();

  searchValue = '';
  filterValues: Record<string, string> = {};

  private _filters: FilterConfig[] = [];
  private searchDebounceTimer?: ReturnType<typeof setTimeout>;

  @Input()
  set filters(value: FilterConfig[]) {
    this._filters = value;
    value.forEach(filter => {
      if (this.filterValues[filter.key] === undefined) {
        this.filterValues[filter.key] = '';
      }
    });
  }

  get filters(): FilterConfig[] {
    return this._filters;
  }

  get visibleFilters(): FilterConfig[] {
    return this._filters.filter(f => f.options.length > 0);
  }

  get hasActiveFilters(): boolean {
    return this.searchValue.trim() !== '' ||
           Object.values(this.filterValues).some(v => v !== '');
  }

  onSearchChange(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.searchChange.emit(this.searchValue);
      this.emitFilterChange();
    }, this.debounceTime);
  }

  onSearchEnter(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchEnter.emit(this.searchValue);
    this.emitFilterChange();
  }

  onFilterChange(): void {
    this.emitFilterChange();
  }

  clearFilters(): void {
    this.searchValue = '';
    Object.keys(this.filterValues).forEach(key => {
      this.filterValues[key] = '';
    });
    this.emitFilterChange();
  }

  private emitFilterChange(): void {
    this.filterChange.emit({
      search: this.searchValue,
      filters: { ...this.filterValues }
    });
  }
}
